import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, CreditCard, Coins, Heart } from 'lucide-react';
import Button from './ui/Button';
import HeroWindow from './HeroWindow';
import useLandingAssets from '../hooks/useLandingAssets';
import { goToApp, IMAGES } from '../constants';

const STATIC_SLIDES = [
  { type: 'image', title: 'AI Product Ad', url: IMAGES.heroProduct },
  { type: 'image', title: 'Social Media Video', url: IMAGES.social },
  { type: 'image', title: 'Cinematic Film', url: IMAGES.cinematic },
];

const PERKS = [
  { icon: CreditCard, text: 'No credit card required' },
  { icon: Coins, text: 'Pay only for what you create' },
  { icon: Heart, text: 'Loved by creators & brands' },
];

// Flatten API hero_banner section into a list of renderable slides.
function buildSlidesFromAssets(heroAssets = []) {
  const slides = [];
  heroAssets.forEach((a) => {
    if (a.media_type === 'carousel') {
      (a.items || []).forEach((it) =>
        slides.push({ type: it.type, url: it.url, external_url: it.url, title: it.caption || a.title || '', thumbnail: a.thumbnail_url })
      );
    } else {
      slides.push({ type: a.media_type, url: a.url, external_url: a.external_url, title: a.title || '', thumbnail: a.thumbnail_url });
    }
  });
  return slides;
}

const Hero = ({ onOpenDemo }) => {
  const sections = useLandingAssets();
  const [active, setActive] = useState(0);

  const slides = useMemo(() => {
    const fromApi = buildSlidesFromAssets(sections?.hero_banner);
    return fromApi.length ? fromApi : STATIC_SLIDES;
  }, [sections]);

  useEffect(() => { setActive(0); }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => setActive((p) => (p + 1) % slides.length), 7000);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <section id="top" className="relative overflow-hidden bg-[#07050d] text-white pt-28 pb-16 lg:pt-32 lg:pb-20" data-testid="hero-section">
      <img src={IMAGES.hero} alt="" className="absolute inset-y-0 right-0 h-full w-full lg:w-[68%] object-cover object-center opacity-60 lg:opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07050d] via-[#07050d]/85 lg:via-[#07050d]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07050d] via-transparent to-[#07050d]/60" />
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-violet-700/30 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mb-5" data-testid="hero-eyebrow">The Creative Operating System</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight" data-testid="hero-headline">
            Your entire<br />creative workflow.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-400 to-purple-300">One AI-powered workspace.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed" data-testid="hero-subheadline">
            Create product ads, social videos, cinematic stories and branded visuals. Start with a simple prompt or take control of every shot.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button size="lg" onClick={goToApp} data-testid="hero-try-free-btn">Start Creating Free <ArrowRight size={18} /></Button>
            <Button variant="outlineDark" size="lg" onClick={onOpenDemo} data-testid="hero-watch-demo-btn">
              <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center"><Play size={12} className="fill-black text-black ml-0.5" /></span>
              Watch 60-Second Demo
            </Button>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
            {PERKS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2 text-xs text-white/70"><Icon size={14} className="text-violet-300" />{text}</li>
            ))}
          </ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
          <span className="hidden xl:block absolute -left-20 top-6 font-script text-4xl text-white/90 -rotate-12 leading-none z-10" aria-hidden>Ideas<br />&nbsp;&nbsp;to Impact</span>
          <HeroWindow slides={slides} active={active} setActive={setActive} onOpenDemo={onOpenDemo} />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
